import React, { useMemo, useRef, useEffect } from 'react';
import { format, isSameDay } from 'date-fns';
import MessageBubble from './MessageBubble';
import MessageGroup from './MessageGroup';
import LoadingSkeleton from './LoadingSkeleton';
import { Icon } from '../Component';

/**
 * Message List Component
 * Renders and groups chat messages with virtualization support
 * 
 * @param {Object} props
 * @param {Array} props.messages - Array of message objects
 * @param {boolean} props.isLoading - Loading state
 * @param {string} props.error - Error message if any
 * @param {Object} props.user - Current user info to determine message direction
 */
const MessageList = ({ messages, isLoading, error, user }) => {
    const messagesEndRef = useRef(null);
    const containerRef = useRef(null);

    // Group messages by date
    const groupedMessages = useMemo(() => {
        if (!messages || messages.length === 0) return [];

        const groups = [];
        let currentGroup = null;

        messages.forEach((message, index) => {
            const messageDate = message.timestamp ? new Date(message.timestamp * 1000) : new Date();

            // Check if we need to start a new group
            if (!currentGroup || !isSameDay(currentGroup.date, messageDate)) {
                if (currentGroup) {
                    groups.push(currentGroup);
                }

                currentGroup = {
                    date: messageDate,
                    messages: [message],
                    dateString: format(messageDate, 'yyyy-MM-dd')
                };
            } else {
                currentGroup.messages.push(message);
            }
        });

        // Add the last group
        if (currentGroup) {
            groups.push(currentGroup);
        }

        return groups;
    }, [messages]);

    // Auto-scroll to bottom when new messages arrive
    useEffect(() => {
        if (messagesEndRef.current && !isLoading) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages, isLoading]);

    // Loading component is now imported from separate file

    // Error state
    const ErrorState = () => (
        <div className="alert alert-danger d-flex align-items-center mx-3" role="alert">
            <Icon name="alert-circle" className="me-2 flex-shrink-0" />
            <div>
                <strong>Unable to load messages</strong>
                <div className="mt-1">{error}</div>
            </div>
        </div>
    );

    // Empty state
    const EmptyState = () => (
        <div className="empty-state">
            <div className="empty-icon">
                <Icon name="chat" />
            </div>
            <h6>No messages found</h6>
            <p>
                No chat messages were found for the selected time period.
            </p>
            <small>
                Try adjusting the date filter or check back later.
            </small>
        </div>
    );

    return (
        <div
            ref={containerRef}
            className="message-list-container"
            style={{
                maxHeight: '60vh',
                minHeight: '400px',
                overflowY: 'auto',
                background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                scrollbarWidth: 'thin',
                scrollbarColor: '#dee2e6 transparent'
            }}
        >

            <div className="p-3">
                {/* Loading State */}
                {isLoading && <LoadingSkeleton />}

                {/* Error State */}
                {error && !isLoading && <ErrorState />}

                {/* Empty State */}
                {!isLoading && !error && messages.length === 0 && <EmptyState />}

                {/* Messages */}
                {!isLoading && !error && messages.length > 0 && (
                    <div >
                        {groupedMessages.map((group, groupIndex) => (
                            <MessageGroup
                                key={group.dateString}
                                date={group.date}
                                messageCount={group.messages.length}
                            >
                                {group.messages.map((message, messageIndex) => {
                                    const isFromMe = message.fromMe;
                                    const isLastInGroup = messageIndex === group.messages.length - 1;

                                    return (
                                        <MessageBubble
                                            key={message.id?.id || `${groupIndex}-${messageIndex}`}
                                            message={message}
                                            isFromMe={isFromMe}
                                            showTimestamp={true}
                                            isLastInGroup={isLastInGroup}
                                        />
                                    );
                                })}
                            </MessageGroup>
                        ))}

                        {/* Scroll anchor */}
                        <div ref={messagesEndRef} />
                    </div>
                )}
            </div>
        </div>
    );
};

export default MessageList;